import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useLanguage } from '@/contexts/LanguageContext';
import { loc } from '@/lib/localize';

export interface HomepageReview {
  id: string;
  author: string;
  avatar: string;
  rating: number;
  review_date: string;
  title: string;
  content: string;
}

interface HomepageReviewRow extends HomepageReview {
  title_vi: string | null;
  content_vi: string | null;
}

export function useHomepageReviews() {
  const [rows, setRows] = useState<HomepageReviewRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { language } = useLanguage();

  useEffect(() => {
    let cancelled = false;

    async function fetchReviews() {
      try {
        setLoading(true);
        setError(null);

        const { data, error: err } = await supabase
          .from('vietnamtravel_homepage_reviews')
          .select('*')
          .order('created_at', { ascending: false });

        if (err) throw err;
        if (!cancelled) setRows((data || []) as HomepageReviewRow[]);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load reviews');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchReviews();
    return () => { cancelled = true; };
  }, []);

  const reviews: HomepageReview[] = rows.map((r) => ({
    id: r.id,
    author: r.author,
    avatar: r.avatar,
    rating: r.rating,
    review_date: r.review_date,
    title: loc(language, r.title, r.title_vi),
    content: loc(language, r.content, r.content_vi),
  }));

  return { reviews, loading, error };
}